import { AxiosResponse } from 'axios';
import {
  InventoryOrderForTireQuote,
  InventoryOrderStatusType,
  PaginatedInput,
  PaginatedResponse,
  StorageObjectFromInventoryOrder
} from 'noddi-types';
import { downloadToExcel } from '../utils';
import { apiVersion, FetchMethods, ResponseType } from './commonResourceTypes';
import { CommonAxiosResType, getNoddiUrl } from './resources';

export const FacilityURLKeys = {
  postStorageObjectFromInventoryOrder: 'postStorageObjectFromInventoryOrder',
  getInventoryOrdersForTireQuote: 'getInventoryOrdersForTireQuote',
  getStorageObjectsToExcel: 'getStorageObjectsToExcel'
} as const;

export type FacilityURLKeysType = keyof typeof FacilityURLKeys;

type InputDataOrVoid<Type extends FacilityURLKeysType> = InputData<Type> extends void ? void : InputData<Type>;

type InputData<urlKey extends FacilityURLKeysType> = FacilityServerRequests[urlKey]['input'];

export const getFacilityUrls: {
  [key in FacilityURLKeysType]: {
    getUrl: (input: InputDataOrVoid<key>) => string;
    getBody?: (input: InputDataOrVoid<key>) => Partial<InputData<key>>;
    method: FetchMethods;
    responseType?: ResponseType;
    handleRes?: (res: CommonAxiosResType<key>) => Promise<unknown>;
    getHeaders?: (input: InputDataOrVoid<key>) => Record<string, string>;
    useSuperUserToken?: boolean;
    skipConvertingToCamelCase?: boolean;
  };
} = {
  postStorageObjectFromInventoryOrder: {
    getUrl() {
      return getNoddiUrl({
        path: `${apiVersion}storage-object-groups/tire-quote-inventory-order/`
      });
    },
    getBody: (data) => data,
    method: FetchMethods.post
  },
  getInventoryOrdersForTireQuote: {
    getUrl(queryParams) {
      return getNoddiUrl({
        path: `${apiVersion}inventory-orders/car-tire-quotes/`,
        queryParams
      });
    },
    method: FetchMethods.get
  },
  getStorageObjectsToExcel: {
    getUrl: ({ storageUnitId, ...queryParams }) =>
      getNoddiUrl({
        path: `${apiVersion}storage-units/${storageUnitId}/storage-objects-to-excel/`,
        queryParams
      }),
    handleRes: async (res) => downloadToExcel(res),
    method: FetchMethods.get,
    responseType: 'arraybuffer'
  }
} as const;

export interface FacilityServerRequests {
  postStorageObjectFromInventoryOrder: {
    input: {
      inventoryOrderId: string;
      storageFacilityId: number;
      storageUnitId: number;
    };
    output: AxiosResponse<StorageObjectFromInventoryOrder>;
  };
  getInventoryOrdersForTireQuote: {
    input: PaginatedInput & { statuses: InventoryOrderStatusType[] | null; storageFacilityIds: number[] | null };
    output: PaginatedResponse<InventoryOrderForTireQuote[]>;
  };
  getStorageObjectsToExcel: {
    input: {
      storageUnitId: number;
    };
    output: ArrayBufferLike;
  };
}
